import React, { useState } from "react";
import { usePost } from "redux/fetch-hooks";

import { CardLevelOne } from "common/DS/ElevatedCard";
import { H4, Subtitle2 } from "common/DS/Text";
import TextField from "common/DS/TextField";
import QuestionButtonGroup from "common/Question/QuestionButtonGroup";
import { heapTrack } from "lib/analytics";
import { Checkbox } from "common/DS/Checkables";

export const CompanyHubAuthCard = (
    {
        title,
        subtitle,
        field1 = "",
        field2 = "",
        companySlug,
        buttonRenamer = {},
        setAuthenticated,
        rememberMeEnabled,
    }: {
        title: string;
        subtitle?: string;
        field1?: string;
        field2?: string;
        companySlug: string;
        buttonRenamer?: object;
        setAuthenticated: (authenticated: boolean) => void;
        rememberMeEnabled?: boolean;
    },
    { t: _ }: { t: (key: string, variables?: object) => string },
) => {
    const field1Placeholder = `Enter your ${field1}`;
    const field1Label = field1;
    const field1Helper = `Please enter a valid ${field1}`;
    const field2Placeholder = `Enter your ${field2}`;
    const field2Label = field2;
    const field2Helper = `Please enter a valid ${field2}`;

    const [hasField1Error, setHasField1Error] = useState(false);
    const [hasField2Error, setHasField2Error] = useState(false);
    const [field1Input, setField1Input] = useState("");
    const [field2Input, setField2Input] = useState("");
    const [field1HelperText, setField1HelperText] = useState("");
    const [field2HelperText, setField2HelperText] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [hasInput, setHasInput] = useState(false);
    const buttonClassName = "hub__login-buttons";
    const [failCount, setFailCount] = useState(0);

    function onField1Change(input: string) {
        const textEntered = input.trim();
        setHasInput(textEntered.length > 0 && !!field2Input);
        setField1Input(textEntered);
        setField1HelperText("");
        setHasField1Error(false);
    }

    function onField2Change(input: string) {
        const textEntered = input.trim();
        setHasInput(textEntered.length > 0 && !!field1Input);
        setField2Input(textEntered);
        setField2HelperText("");
        setHasField2Error(false);
    }

    function onResend() {
        setField1Input("");
        setField2Input("");
        setField1HelperText("");
        setField2HelperText("");
        setHasField1Error(false);
        setHasField2Error(false);
    }

    const [postAuthCode, { isLoading }] = usePost(`/check-in/auth/code/verify/${companySlug}`, {
        onSuccess: () => {
            heapTrack("Hub 2.0 Login", { companySlug });
            setAuthenticated(true);
        },
        onError: (error: Error) => {
            if (error?.message) {
                setField2HelperText(error.message);
                setField1HelperText("");
                setHasField1Error(false);
                setHasField2Error(true);
                setFailCount(failCount + 1);

                if (failCount >= 3) {
                    window.location.replace("/hub/");
                }
            }
        },
    });

    function onField1Blur() {
        if (!field1Input.length) {
            setField1HelperText(field1Helper);
            setHasField1Error(true);
        }
    }

    function onField2Blur() {
        if (!field2Input.length) {
            setField2HelperText(field2Helper);
            setHasField2Error(true);
        }
    }

    function onNext() {
        setHasField1Error(false);
        setHasField2Error(false);
        setField1HelperText("");
        setField2HelperText("");

        postAuthCode({
            data: {
                csrf_token: window.csrfToken,
                field1: field1Input,
                field2: field2Input,
                remember_me: rememberMe,
            },
        });
    }

    return (
        <CardLevelOne className="hub__login">
            <H4>{title}</H4>
            <Subtitle2 className="hub__login-subtitle">{subtitle}</Subtitle2>
            <TextField
                className="hub__login-text-box"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={field1Input}
                placeholder={field1Placeholder}
                onChange={(e) => onField1Change(e.target.value)}
                onBlur={() => onField1Blur()}
                label={field1Input ? field1Label : ""}
                helperText={field1HelperText}
                error={hasField1Error}
            />
            <TextField
                className="hub__login-text-box"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={field2Input}
                placeholder={field2Placeholder}
                onChange={(e) => onField2Change(e.target.value)}
                onBlur={() => onField2Blur()}
                label={field2Input ? field2Label : ""}
                helperText={field2HelperText}
                error={hasField2Error}
            />
            {rememberMeEnabled && (
                <Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}>
                    <span>Remember me</span>
                </Checkbox>
            )}
            <QuestionButtonGroup
                handleSubmit={onNext}
                handleSkip={onResend}
                nextDisabled={!hasInput || isLoading}
                additionalClassNames={buttonClassName}
                buttonRenamer={buttonRenamer}
            />
        </CardLevelOne>
    );
};
